
import { useRef, useState } from "react";
import "./textDropDown.scss";
import RenameFile from "components/RenameFile/index";
import { format } from "date-fns";
import { Folder as IzFolder, Task } from "@induz/sdk";
import { useToast } from "components/Toaster/index";
import { ensureFileType } from "utils/fileTypeHelper";
import { normalizeName } from "utils/normalizeName";
const TextDropDown = ({
  content,
  width = "",
  setFilesList,
  setModal,
  setUploadedFiles,
  setIsDropDownVisible,
  setAddFile = () => { },
  setSelectedFile = [],
  fetchData,
  path,
  setUploadFolder,
  filesWrap = false,
  taskManager = null
}) => {
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const showToast = useToast();
  const [folderName, setFolderName] = useState(""); // Store folder name
  const [createFolderState, setCreateFolderState] = useState(false);

  const handleFileChange = (e) => {
    const rawFiles = Array.from(e.target.files);
    const files = rawFiles.map(ensureFileType);
    setFilesList(() => [...files]); // Replace with new files
    setSelectedFile(() => [...files]);
    // Reset input value to allow selecting the same files again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // const handleFolderChange = (e) => {
  //   const files = Array.from(e.target.files);
  //   setFilesList((prevFiles) => [...prevFiles, ...files]);
  // };

  // For folder uploads, skip local processing entirely and dispatch directly to
  // FilesWrapper's handleFolderUploadEvent which has chunked async processing + overlay
  const handleFolderChange = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Pass raw FileList directly — no Array.from to avoid freezing on 60K files.
    // Input reset is deferred until scanning finishes so the FileList stays valid.
    window.dispatchEvent(new CustomEvent("uploadFolder", { detail: { files } }));

    // Close the dropdown
    setIsDropDownVisible(false);
    setAddFile(false);

    // DON'T reset input here — it invalidates the live FileList.
    // FilesWrapper dispatches "folderScanComplete" when it's done reading.
    const resetInput = () => {
      if (folderInputRef.current) {
        folderInputRef.current.value = "";
      }
    };
    window.addEventListener("folderScanComplete", resetInput, { once: true });
  };

  const triggerFileInput = (type) => {
    if (type === "file") {
      setUploadFolder(false);
      fileInputRef.current?.click();
    } else if (type === "folder") {
      setUploadFolder(true);
      folderInputRef.current?.click();
    }
  };

  const saveHandler = async () => {
    if (!folderName.trim()) return;
    const now = new Date();
    let dupCurrentPath = path;
    if (dupCurrentPath[0] === "shared" && dupCurrentPath.length >= 2) {
      const newPath = [...dupCurrentPath];
      newPath.splice(1, 1);
      dupCurrentPath = newPath;
    }
    const formattedDateTime = format(now, "M/d/yyyy h:mm a"); // Uses Date-fns
    const formattedPath = dupCurrentPath?.length === 0 ? "/" : `/${dupCurrentPath.join("/")}`;
    // const sdk = new Sdk(URL);

    const sanitizedFolderName = normalizeName(folderName, false);
    // Create task for folder creation if taskManager is available
    const folderTask = taskManager ? new Task(0, `Creating ${sanitizedFolderName}`, "Creating") : null;
    if (folderTask) {
      taskManager.addRunningTask(folderTask);
    }

    setFolderName("");
    setCreateFolderState(false);
    setIsDropDownVisible(false);
    setAddFile(false);

    try {
      const root = new IzFolder().getRoot();
      await root.createFolder(formattedPath, sanitizedFolderName);
      const newFolder = {
        name: sanitizedFolderName,
        type: "folder",
        files: [],
        lastModified: formattedDateTime,
        size: 0,
        owner: "You",
        filePermission: "owner"
      };

      setUploadedFiles((prevFiles) => [...prevFiles, newFolder]);

      if (folderTask && taskManager) {
        folderTask.progress = 100;
        taskManager.addCompletedTask(folderTask, 1);
      }

      fetchData(path);
    } catch (error) {
      // Mark task as failed if taskManager is available
      if (folderTask && taskManager) {
        taskManager.addCompletedTask(folderTask, 2);
      }
      console.error("Failed to create folder:", error);
      showToast("Failed to create folder", "error");
    }
  };

  const cancelHandler = () => {
    setFolderName(""); // Reset input field
    setCreateFolderState(false);
    setIsDropDownVisible(false);
    setAddFile(false);
  };

  return (
    <>
      {!createFolderState && (
        <div className="textDropDownContainer" style={{ width: width }}>
          {content.map((item, index) => (
            <div
              className="option"
              key={index}
              onClick={() => {
                if (item.onClick) {
                  item.onClick();
                }
                if (item.title === "Upload File") {
                  setFilesList([]); // Clear previous files before opening new upload
                  triggerFileInput("file");
                  setModal(true);
                } else if (item.title === "Upload Folder") {
                  triggerFileInput("folder");
                  // Modal not needed - folder uploads go directly via window event
                } else if (item.title === "Create Folder") {
                  setModal(false);
                  setCreateFolderState(true);
                }
              }}
            >
              {item.title}
            </div>
          ))}
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        multiple
      />
      <input
        type="file"
        ref={folderInputRef}
        style={{ display: "none" }}
        onChange={handleFolderChange}
        webkitdirectory="true"
        // eslint-disable-next-line react/no-unknown-property
        directory="true"
        multiple
      />

      {createFolderState && (
        <div style={{ minHeight: "187px", height: "auto" }}>
          <RenameFile
            inputWidth="89%"
            isVisible={createFolderState}
            submitBtnText="Create"
            // label="Create Folder"
            placeholder="Folder name"
            autoFocus
            value={folderName}
            onChange={(e) => {
              // eslint-disable-next-line no-useless-escape
              const newValue = e.target.value.replace(
                /[^a-zA-Z0-9 _-]/g,
                ""
              ); // Removed unnecessary escape
              setFolderName(newValue);
            }}
            handleSave={saveHandler}
            handleCancel={cancelHandler}
            setFilesList={setFilesList}
            heading="Create Folder"
            wrapWidth="100%"
            disabled={folderName.length === 0}
            maxLength={80}
            onKeyDown={(e) => {
              if (e.key === "Enter" && folderName.trim()) {
                e.preventDefault();
                saveHandler();
              }


            }}
          />
        </div>
      )}
    </>
  );
};

export default TextDropDown;
